import _ from 'lodash';
import { sqTrendScalarStore, sqTrendSeriesStore } from '@/core/core.stores';
import { Store } from '@/core/flux.service';
import { findItemIn } from '@/trend/trendDataHelper.utilities';
import type { InitializeMode, PersistenceLevel } from '@/core/flux.service';
import type { TreemapItem } from '@/treemap/TreemapChart.atom';

export type TreemapParent = { id?: string };

export type TreemapBreadcrumbs = { id: string; name: string }[];

export interface TreemapStatisticSelection {
  index: number;
  id?: string | number;
  key?: string | null;
}

interface TreemapState {
  swap: Partial<{ id: string; name: string }>;
  parent: TreemapParent;
  breadcrumbs: TreemapBreadcrumbs;
  tree: TreemapItem[];
  isFetching: boolean;
  helpKey?: string;
  statistics: Array<TreemapStatisticSelection | undefined>;
  priorityColors: string[];
  neutralColor?: string;
  proportionBySignal?: string;
  proportionByColumn?: string;
  isUniformSizing: boolean;
  treemapSquareScalingInverse: boolean;
}

export type TreemapDehydratedState = Pick<
  TreemapState,
  | 'parent'
  | 'statistics'
  | 'proportionBySignal'
  | 'proportionByColumn'
  | 'isUniformSizing'
  | 'treemapSquareScalingInverse'
>;

export class TreemapStore extends Store {
  static readonly storeName = 'sqTreemapStore';
  persistenceLevel: PersistenceLevel = 'WORKSHEET';

  /**
   * Initializes the store by setting default values to the stored state
   */
  initialize(initializeMode: InitializeMode): void {
    const saveState = this.state && initializeMode !== 'FORCE';
    const initialState: TreemapState = {
      swap: {},
      parent: {},
      breadcrumbs: [],
      tree: [],
      isFetching: false,
      helpKey: undefined,
      statistics: [],
      priorityColors: saveState ? this.state.get('priorityColors') : [],
      neutralColor: saveState ? this.state.get('neutralColor') : undefined,
      proportionBySignal: undefined,
      proportionByColumn: undefined,
      isUniformSizing: false,
      treemapSquareScalingInverse: false,
    };
    this.state = this.immutable(initialState);
  }

  get swap(): Partial<{ id: string; name: string }> {
    return this.state.get('swap');
  }

  get parent(): TreemapParent {
    return this.state.get('parent');
  }

  get breadcrumbs(): TreemapBreadcrumbs {
    return this.state.get('breadcrumbs');
  }

  get tree(): TreemapItem[] {
    return this.state.get('tree');
  }

  get helpKey(): string | undefined {
    return this.state.get('helpKey');
  }

  get statistics(): Array<TreemapStatisticSelection | undefined> {
    return this.state.get('statistics');
  }

  /**
   * Returns an array that is ordered by index of only those statistics that have both id and key.
   */
  get validStatistics(): TreemapStatisticSelection[] {
    const statistics = this.state.get('statistics') as Array<TreemapStatisticSelection | undefined>;

    return _.chain(statistics)
      .filter((statistic): statistic is TreemapStatisticSelection => {
        if (!statistic) {
          return false;
        }

        if (_.isNil(statistic.key) || _.isNil(statistic.id)) {
          return false;
        }

        return !_.isEmpty(findItemIn([sqTrendSeriesStore, sqTrendScalarStore], statistic.id));
      })
      .sortBy('index')
      .value();
  }

  get priorityColors(): string[] {
    return this.state.get('priorityColors');
  }

  get neutralColor(): string | undefined {
    return this.state.get('neutralColor');
  }

  get proportionBySignal(): string | undefined {
    return this.state.get('proportionBySignal');
  }

  get proportionByColumn(): string | undefined {
    return this.state.get('proportionByColumn');
  }

  get isUniformSizing(): boolean {
    return this.state.get('isUniformSizing');
  }

  get treemapSquareScalingInverse(): boolean {
    return this.state.get('treemapSquareScalingInverse');
  }

  /**
   * Sets the asset that is being swapped out. Invalidates the parent when it changes since a new swap out asset
   * means a new hierarchy.
   *
   * @param {Object} payload - Object container for arguments
   */
  private setSwap = (payload: {
    /** The ID of the asset */
    id: string;
    /** The name of the asset */
    name: string;
  }): void => {
    if (!_.isEmpty(this.state.get('swap')) && payload.id !== this.state.get('swap').id) {
      this.state.set('parent', {});
    }

    this.state.set('swap', payload);
  };

  /**
   * Sets the parent asset for swapping, the children underneath it will be displayed in the treemap. Since setting
   * the parent changes the breadcrumbs those are cleared.
   *
   * @param {Object} payload - Object container for arguments
   */
  private setParent = (payload: TreemapParent): void => {
    this.state.set('parent', payload);
    this.state.set('breadcrumbs', []);
    this.state.set('tree', []);
  };

  /**
   * Sets the breadcrumbs that help the user navigate to the current parent.
   *
   * @param payload - Object container for arguments
   * @param payload.breadcrumbs An array of assets that lead up to the current parent.
   */
  private setBreadcrumbs = ({ breadcrumbs }: { breadcrumbs: TreemapBreadcrumbs }): void => {
    this.state.set('breadcrumbs', breadcrumbs);
  };

  /**
   * Sets the tree data for generating the treemap.
   *
   * @param {Object} payload - Object container for arguments
   * @param {Object[]} payload.tree - An array of data for each asset.
   * @param {Object} payload.tree[].asset - The asset to which this result corresponds.
   * @param {String} payload.tree[].color - The color of the box. Should be one of the treemap colors.
   * @param {Number} payload.tree[].size - The relative box size.
   * @param {Boolean} payload.tree[].isLeafAsset - True if it is a leaf asset, false otherwise.
   */
  private setTree = (payload: any): void => {
    this.state.set('tree', payload.tree);
    this.state.set('helpKey', undefined);
  };

  /**
   * Sets the translation key that tells the user why the treemap cannot be loaded.
   *
   * @param {Object} payload - Object container for arguments
   */
  private setHelpKey = (payload: {
    /** A translation key that tells the user why the treemap can't be loaded. */
    helpKey: string;
  }): void => {
    this.state.set('helpKey', payload.helpKey);
  };

  /**
   * Set a statistic to be displayed on each asset node.
   *
   * @param {Object} payload - Object container for arguments
   */
  private setStatistic = (payload: TreemapStatisticSelection): void => {
    const currentStatistic = this.state.get('statistics')[payload.index];
    this.state.set(['statistics', payload.index], {
      ...currentStatistic,
      ...payload,
    });
  };

  /**
   * Sets the colors for the treemap priorities.
   *
   * @param {Object} payload - Object container for arguments
   */
  private setColors = (payload: {
    /** The list of colors, ordered by priority from highest to lowest, with the neutral color last. */
    colors: string[];
  }): void => {
    this.state.set('priorityColors', _.initial(payload.colors));
    this.state.set('neutralColor', _.last(payload.colors));
  };

  private setProportionBySignal = (payload: { proportionBy: string | null }): void => {
    this.state.set('proportionBySignal', payload.proportionBy);
  };

  private setProportionByColumn = (payload: { proportionBy: string | null }): void => {
    this.state.set('proportionByColumn', payload.proportionBy);
  };

  private setIsUniformSizing = (payload: { isUniformSizing: boolean }): void => {
    this.state.set('isUniformSizing', payload.isUniformSizing);
  };

  private setTreemapSquareScalingInverse = (payload: { inverse: boolean }): void => {
    this.state.set('treemapSquareScalingInverse', payload.inverse);
  };

  /**
   * Updates the selected statistics when items in the details are asset swapped.
   *
   * @param {Object} payload - Object container for arguments
   * @param {Object} payload.swaps - The items that were swapped where the keys are the swapped out ids and the
   *   values are the corresponding swapped in ids.
   * @param {Object} payload.outAsset - Asset that was swapped out
   * @param {String} payload.outAsset.id - The ID of the asset to swapped out
   * @param {String} payload.inAsset.name - The name of the asset that was swapped out
   * @param {Object} payload.inAsset - Asset that was swapped in
   * @param {String} payload.inAsset.id - The ID of the asset that was swapped in
   * @param {String} payload.inAsset.name - The name of the asset that was swapped in
   */
  private swapItems = ({ swaps }: any): void => {
    const statistics = this.state.get(['statistics']) as Array<TreemapStatisticSelection | undefined>;
    _.forEach(statistics, (statistic, index) => {
      if (!statistic?.id) {
        return;
      }

      const swappedId = swaps[statistic.id];
      if (swappedId) {
        this.state.set(['statistics', index, 'id'], swappedId);
      }
    });
  };

  protected readonly handlers = {
    TREEMAP_SET_SWAP: this.setSwap,
    TREEMAP_SET_PARENT: this.setParent,
    TREEMAP_SET_BREADCRUMBS: this.setBreadcrumbs,
    TREEMAP_SET_TREE: this.setTree,
    TREEMAP_SET_HELP_KEY: this.setHelpKey,
    TREEMAP_SET_STATISTIC: this.setStatistic,
    TREEMAP_SET_COLORS: this.setColors,
    TREND_SWAP_ITEMS: this.swapItems,
    TREEMAP_SET_SIGNAL_TO_PROPORTION_BY: this.setProportionBySignal,
    TREEMAP_SET_COLUMN_TO_PROPORTION_BY: this.setProportionByColumn,
    TREEMAP_SET_IS_UNIFORM_SIZING: this.setIsUniformSizing,
    TREEMAP_SET_SQUARE_SCALING_INVERSE: this.setTreemapSquareScalingInverse,
  };

  /**
   * Dehydrates the item by retrieving the current set parameters in view.
   *
   * @returns TreemapDehydratedState An object with the state properties as JSON
   */
  dehydrate(): TreemapDehydratedState {
    const { parent, statistics, proportionBySignal, proportionByColumn, isUniformSizing, treemapSquareScalingInverse } =
      this.state.serialize() as TreemapState;

    return {
      parent,
      statistics,
      proportionBySignal,
      proportionByColumn,
      isUniformSizing,
      treemapSquareScalingInverse,
    };
  }

  /**
   * Rehydrates the store from dehydrated state.
   *
   * @param dehydratedState - State object that should be restored
   */
  rehydrate(dehydratedState: Partial<TreemapDehydratedState>): void {
    this.state.merge(dehydratedState);
  }
}
